const { isBirthDateQuestion, birthDateError } = require('./dateValue');

function isFalseLike(value) {
  if (value === false || value === 0) return true;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return normalized === 'false' || normalized === '0' || normalized === 'non';
  }
  return false;
}

function hasAnswerValue(value) {
  if (value === undefined || value === null) return false;
  if (typeof value === 'string') return value.trim() !== '';
  return true;
}

function flattenQuestions(source, acc = []) {
  if (!source) return acc;

  if (Array.isArray(source)) {
    source.forEach((item) => flattenQuestions(item, acc));
    return acc;
  }

  if (Array.isArray(source.data)) {
    flattenQuestions(source.data, acc);
  }
  if (Array.isArray(source.criteres)) {
    flattenQuestions(source.criteres, acc);
  }
  if (Array.isArray(source.sousCriteres)) {
    flattenQuestions(source.sousCriteres, acc);
  }
  if (Array.isArray(source.questions)) {
    flattenQuestions(source.questions, acc);
  }

  const hasChildren = Array.isArray(source.data)
    || Array.isArray(source.criteres)
    || Array.isArray(source.sousCriteres)
    || Array.isArray(source.questions);

  if (!hasChildren && source.id !== undefined && source.id !== null) {
    acc.push(source);
  }

  return acc;
}

function buildQuestionLookup(source) {
  if (source instanceof Map) return source;

  const lookup = new Map();
  flattenQuestions(source).forEach((question) => {
    const key = String(question.id);
    lookup.set(key, question);
  });
  return lookup;
}

function hasRequiredAlternanceAcceptance(period) {
  const alternanceValue = String(period?.filtres?.[5] || "").trim().toLowerCase();
  if (!alternanceValue.startsWith("alternance")) return true;

  const acceptanceQuestion = buildQuestionLookup(period).get("513");
  return String(acceptanceQuestion?.value || "").trim().toLowerCase() === "oui";
}

function isDependencySatisfied(question, source) {
  const dep = question?.Dépendance;
  if (!Array.isArray(dep) || dep.length < 2) return true;

  const questionLookup = buildQuestionLookup(source);
  const depQuestion = questionLookup.get(String(dep[0]));
  if (!depQuestion) return false;

  let ok = String(depQuestion.value) === String(dep[1]);

  const dep2 = question?.Dépendance2;
  if (ok && Array.isArray(dep2) && dep2.length >= 2) {
    const depQuestion2 = questionLookup.get(String(dep2[0]));
    if (!depQuestion2) return false;
    ok = ok && (String(depQuestion2.value) === String(dep2[1]));
  }

  return ok;
}

function hasDynamicVisibility(question) {
  const hasDeps = (Array.isArray(question?.Dépendance) && question.Dépendance.length >= 2)
    || (Array.isArray(question?.Dépendance2) && question.Dépendance2.length >= 2);
  const hasFilters = Array.isArray(question?.filtres)
    && question.filtres.some((group) => Array.isArray(group) && group.length > 0);
  return hasDeps || hasFilters;
}

function isFilterSatisfied(item, dataFiltres) {
  const filtres = item?.filtres;
  if (!Array.isArray(filtres)) return true;

  return filtres.every((group, groupIndex) => {
    if (!Array.isArray(group) || group.length === 0) return true;
    if (!Array.isArray(dataFiltres) || dataFiltres[groupIndex] == null) return false;
    return group.some((value) => value === dataFiltres[groupIndex]);
  });
}

function getVisibilityDataFiltres(options = {}) {
  if (Array.isArray(options.dataFiltres)) return options.dataFiltres;
  if (Array.isArray(options.period?.filtres)) return options.period.filtres;
  if (Array.isArray(options.source?.filtres)) return options.source.filtres;
  return [];
}

function getVisibilityQuestionLookup(options = {}, fallbackSource = null) {
  if (options.questionLookup instanceof Map) return options.questionLookup;
  return buildQuestionLookup(options.period || options.source || options.questions || fallbackSource);
}

function isQuestionVisible(question, options = {}) {
  const dataFiltres = getVisibilityDataFiltres(options);
  const source = options.questionLookup || options.questions || options.period || options.source;
  if (!isFilterSatisfied(question, dataFiltres)) return false;
  if (!isDependencySatisfied(question, source)) return false;
  return true;
}

function isQuestionCounted(question, options = {}) {
  return question?.required === 'required' && isQuestionVisible(question, options);
}

function isQuestionAnswered(question) {
  // Revalider aussi les réponses anciennes, sans autoriser un ancien drapeau
  // de confirmation à contourner la limite d'âge.
  if (isBirthDateQuestion(question) && birthDateError(question.value)) return false;

  if (question?.type === 'paiement') {
    return question.value === true || String(question.value).trim().toLowerCase() === 'true';
  }

  if (question?.type === 'search') {
    if (!hasAnswerValue(question.value) && !hasAnswerValue(question.valueName)) {
      return false;
    }
  } else if (!hasAnswerValue(question?.value)) {
    return false;
  }

  return question?.valideFormat === undefined
    || question?.valideFormat === true
    || question?.valideFormatConfirm === true;
}

function computeSousCriterePercentage(sousCritere, options = {}) {
  if (!isSousCritereVisible(sousCritere, options)) {
    return 100;
  }

  // Le pourcentage ne dépend que des réponses. L'indicateur `vue` (sous-critère
  // ouvert ou non) n'entre plus en compte : il n'était enregistré qu'au gré de
  // la navigation et un sous-critère complet retombait à 0 % au rechargement.
  const dataFiltres = getVisibilityDataFiltres(options);
  const questionLookup = getVisibilityQuestionLookup(options, sousCritere);
  const questions = Array.isArray(sousCritere?.questions) ? sousCritere.questions : [];
  const requiredAndVisible = questions.filter((question) =>
    isQuestionCounted(question, {
      dataFiltres,
      questionLookup
    })
  );

  const totalQuestions = requiredAndVisible.length;
  if (totalQuestions === 0) return 100;

  const answeredQuestions = requiredAndVisible.filter(isQuestionAnswered).length;
  return Math.round((answeredQuestions / totalQuestions) * 100);
}

function isSousCritereVisible(sousCritere, options = {}) {
  const dataFiltres = getVisibilityDataFiltres(options);
  const critere = options.critere;
  if (critere && !isFilterSatisfied(critere, dataFiltres)) return false;
  if (!isFilterSatisfied(sousCritere, dataFiltres)) return false;

  const questions = Array.isArray(sousCritere?.questions) ? sousCritere.questions : [];
  if (questions.length === 0) return false;

  const questionLookup = getVisibilityQuestionLookup(options, sousCritere);
  return questions.some((question) => isQuestionVisible(question, {
    dataFiltres,
    questionLookup
  }));
}

function normalizeSousCritereApplicableOptions(dataFiltresOrOptions, sourceOrOptions) {
  if (
    dataFiltresOrOptions
    && typeof dataFiltresOrOptions === "object"
    && !Array.isArray(dataFiltresOrOptions)
    && !(dataFiltresOrOptions instanceof Map)
  ) {
    return dataFiltresOrOptions;
  }

  const options = {
    dataFiltres: Array.isArray(dataFiltresOrOptions) ? dataFiltresOrOptions : []
  };

  if (sourceOrOptions instanceof Map) {
    options.questionLookup = sourceOrOptions;
  } else if (
    sourceOrOptions
    && typeof sourceOrOptions === "object"
    && !Array.isArray(sourceOrOptions)
  ) {
    if (
      Array.isArray(sourceOrOptions.dataFiltres)
      || sourceOrOptions.questionLookup instanceof Map
      || sourceOrOptions.period
      || sourceOrOptions.source
      || sourceOrOptions.questions
      || sourceOrOptions.critere
    ) {
      Object.assign(options, sourceOrOptions);
    } else {
      options.source = sourceOrOptions;
    }
  }

  return options;
}

function isSousCritereApplicable(sousCritere, dataFiltresOrOptions, sourceOrOptions) {
  return isSousCritereVisible(
    sousCritere,
    normalizeSousCritereApplicableOptions(dataFiltresOrOptions, sourceOrOptions)
  );
}

function isCritereVisible(critere, options = {}) {
  const dataFiltres = getVisibilityDataFiltres(options);
  if (!isFilterSatisfied(critere, dataFiltres)) return false;

  const sousCriteres = Array.isArray(critere?.sousCriteres) ? critere.sousCriteres : [];
  if (sousCriteres.length === 0) return false;

  const questionLookup = getVisibilityQuestionLookup(options, critere);
  return sousCriteres.some((sousCritere) => isSousCritereVisible(sousCritere, {
    ...options,
    dataFiltres,
    questionLookup,
    critere
  }));
}

function getVisibleSousCritereEntries(period, options = {}) {
  const dataFiltres = getVisibilityDataFiltres({ ...options, period });
  const questionLookup = getVisibilityQuestionLookup({ ...options, period }, period);
  const criteres = Array.isArray(period?.criteres) ? period.criteres : [];
  const entries = [];

  criteres.forEach((critere, critereIndex) => {
    if (!isFilterSatisfied(critere, dataFiltres)) return;

    const sousCriteres = Array.isArray(critere?.sousCriteres) ? critere.sousCriteres : [];
    sousCriteres.forEach((sousCritere, sousCritereIndex) => {
      if (!isSousCritereVisible(sousCritere, {
        ...options,
        dataFiltres,
        questionLookup,
        critere,
        period
      })) {
        return;
      }

      entries.push({
        critere,
        sousCritere,
        critereIndex,
        sousCritereIndex
      });
    });
  });

  return entries;
}

function recalculateProgressForDossier(dossierData) {
  if (!dossierData || !Array.isArray(dossierData.data)) {
    return { changed: false, data: dossierData };
  }

  let changed = false;

  dossierData.data.forEach((period) => {
    const dataFiltres = Array.isArray(period?.filtres) ? period.filtres : [];
    const questionLookup = buildQuestionLookup(period);
    const criteres = Array.isArray(period?.criteres) ? period.criteres : [];

    criteres.forEach((critere) => {
      const sousCriteres = Array.isArray(critere?.sousCriteres) ? critere.sousCriteres : [];
      sousCriteres.forEach((sousCritere) => {
        const nextPercentage = computeSousCriterePercentage(sousCritere, {
          dataFiltres,
          questionLookup,
          critere,
          period
        });
        if (sousCritere.pourcentage !== nextPercentage) {
          sousCritere.pourcentage = nextPercentage;
          changed = true;
        }
      });
    });
  });

  return { changed, data: dossierData };
}

function synchronizeQuestionVisibility(dossierData) {
  if (!dossierData || !Array.isArray(dossierData.data)) {
    return { changed: false, data: dossierData };
  }

  let changed = false;

  dossierData.data.forEach((period) => {
    const dataFiltres = Array.isArray(period?.filtres) ? period.filtres : [];
    const questionLookup = buildQuestionLookup(period);
    const criteres = Array.isArray(period?.criteres) ? period.criteres : [];

    criteres.forEach((critere) => {
      const critereFilterSatisfied = isFilterSatisfied(critere, dataFiltres);
      const sousCriteres = Array.isArray(critere?.sousCriteres) ? critere.sousCriteres : [];
      sousCriteres.forEach((sousCritere) => {
        const sousCritereFilterSatisfied = critereFilterSatisfied && isFilterSatisfied(sousCritere, dataFiltres);
        const questions = Array.isArray(sousCritere?.questions) ? sousCritere.questions : [];
        questions.forEach((question) => {
          const nextVisible = sousCritereFilterSatisfied
            && isQuestionVisible(question, {
              dataFiltres,
              questionLookup
            });

          if (question.visible !== nextVisible) {
            question.visible = nextVisible;
            changed = true;
          }

          if (
            question.visiblepourcentage !== undefined
            && question.visiblepourcentage !== nextVisible
          ) {
            question.visiblepourcentage = nextVisible;
            changed = true;
          }
        });
      });
    });
  });

  return { changed, data: dossierData };
}

module.exports = {
  buildQuestionLookup,
  computeSousCriterePercentage,
  getVisibleSousCritereEntries,
  hasDynamicVisibility,
  hasRequiredAlternanceAcceptance,
  isCritereVisible,
  isDependencySatisfied,
  isFilterSatisfied,
  isQuestionAnswered,
  isQuestionCounted,
  isQuestionVisible,
  isSousCritereApplicable,
  isSousCritereVisible,
  recalculateProgressForDossier,
  synchronizeQuestionVisibility
};
